/**
 * ChatView.tsx - Main Chat Display View
 *
 * Primary view for displaying chat conversations.
 * Shows: chat thread, loading states, empty states, and footer input.
 */

import { Footer } from '@/ui/layout/Footer/Footer';
import { ChatThread } from './ChatThread';
import { useLoadingStates } from '@/hooks/useLoadingStates';
import { flattenAllResponses } from '@/hooks/chat.utils';
import { useChatContext, useChatRefs, useStreamingState } from '@/contexts/ChatContext';
import {
    Box,
    Card,
    EmptyState,
    HStack,
    Skeleton,
    Stack,
    Text,
    VStack
} from "@chakra-ui/react";
import {
    useLoadResponses
} from "core";
import cairLogo from '/cair.svg';
import { t } from 'i18next';
import { ReactFC } from '@/types/chat.types';
import { UserFirstName } from '@/ui/components/UserFirstName';
import { SuggestedQuestionsHeader } from '@/ui/components/SuggestedQuestionsWidget';
import { SuggestionsPanel } from '@/ui/components/SuggestionsPanel';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StreamingBubble } from '@/ui/components/StreamingBubble';

export const ChatView: ReactFC = () => {
    const {
        post,
        postId,
        setLatestResponseId,
        refreshChat,
        loading,
        setLoading,
        waitingForAIResponseId,
        setWaitingForAIResponseId,
        setStreamingAssistantPostId,
        setStreamingText,
        setStreamingVectorSearchResults,
    } = useChatContext();

    const { messagesEndRef } = useChatRefs();
    const { streamingAssistantPostId, streamingText, streamingVectorSearchResults } = useStreamingState();

    const { i18n } = useTranslation();
    const isRTL = i18n.dir() === 'rtl';
    
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [userHasScrolledUp, setUserHasScrolledUp] = useState(false);
    const isAutoScrollingRef = useRef(false);
    
    
    const isNearBottom = (element: HTMLElement, threshold = 100) => {
        return element.scrollHeight - element.scrollTop - element.clientHeight < threshold;
    };
    
    const handleScroll = () => {
        if (isAutoScrollingRef.current) return;
        const container = scrollContainerRef.current;
        if (!container) return;
        setUserHasScrolledUp(!isNearBottom(container, 50));
    };
    
    const scrollToBottom = (smooth = true) => {
        const container = scrollContainerRef.current;
        if (!container) return;
        isAutoScrollingRef.current = true;
        container.scrollTo({
            top: container.scrollHeight,
            behavior: smooth ? 'smooth' : 'auto'
        });
        setTimeout(() => {
            isAutoScrollingRef.current = false;
        }, 100);
    };

    const { data: postResponses } = useLoadResponses(postId, {
        refreshInterval: postId && loading ? 3000 : 0,
        revalidateOnFocus: true,
        revalidateOnReconnect: true,
        dedupingInterval: 2000,
    });

    // Extract responses without calling setState during render
    const responseList = useMemo(() => {
        const response_list: any[] = [];
        const responsesObject = postResponses?.responses;
        if (responsesObject !== undefined) {
            for (let key in responsesObject) {
                let value = responsesObject[key];
                response_list.push(value);
            }
        }
        return response_list;
    }, [postResponses]);

    const allResponses = useMemo(() => flattenAllResponses(responseList), [responseList]);

    // Update latestResponseId in useEffect to avoid setState during render
    useEffect(() => {
        if (responseList.length > 0) {
            const lastResponse = responseList[responseList.length - 1];
            if (!postId || !lastResponse.thread || lastResponse.thread.length === 0 || lastResponse.thread[0] === postId) {
                setLatestResponseId(lastResponse._id);
            }
        }
    }, [responseList, postId, setLatestResponseId]);

    const shouldShowLoadingIndicator = useMemo(() => {
        if (!loading) return false;
        
        // If there's no streaming happening at all, show loading
        if (!streamingAssistantPostId || !streamingText) return true;
        
        // If the streaming post already exists in responses, 
        // it means we're waiting for a NEW stream to start
        const streamingPostExistsInResponses = allResponses.some(
            (r: any) => r._id === streamingAssistantPostId
        );
        
        // Show loading if the current streaming post is an OLD post (exists in responses)
        // This means we're waiting for the NEW stream to produce content
        return streamingPostExistsInResponses;
    }, [loading, streamingAssistantPostId, streamingText, allResponses]);
    

    useEffect(() => {
        if (!loading || !waitingForAIResponseId) return;
        const waitingForAI: any = allResponses.find((r: any) => r._id === waitingForAIResponseId);
        if (!waitingForAI) return;

        const status = waitingForAI?.metadata?.aiProcessingStatus;
        if (status === 'complete' || status === 'error') {
            setLoading(false);
            setWaitingForAIResponseId(null);
        }
    }, [allResponses, loading, waitingForAIResponseId, setLoading, setWaitingForAIResponseId]);

    const sourcesFound = allResponses.some(
        (response: any) => response?.vector_search_results && response.vector_search_results.length > 0
    );

    const { loadingText, loadingDots } = useLoadingStates(loading && !sourcesFound);
    
    // ⚠️ CRITICAL FIX: Check if streaming post exists in responses more accurately
    const isStreamingNewPost = useMemo(() => {
        // If no streaming is happening, return false
        if (!streamingAssistantPostId || !streamingText) return false;
        
        // Check if the streaming post already exists in the responses
        const existsInResponses = allResponses.some((r: any) => r._id === streamingAssistantPostId);
        
        // Only show StreamingBubble if the post doesn't exist yet
        return !existsInResponses;
    }, [streamingAssistantPostId, streamingText, allResponses]);
    
    useEffect(() => {
        if (streamingText && !userHasScrolledUp) {
            scrollToBottom(true);
        }
    }, [streamingText, userHasScrolledUp]);
    
    useEffect(() => {
        if (allResponses.length > 0 && !userHasScrolledUp) {
            scrollToBottom(true);
        }
    }, [allResponses.length, userHasScrolledUp]);
    
    useEffect(() => {
        setUserHasScrolledUp(false);
        setTimeout(() => scrollToBottom(false), 100);
    }, [postId]);

    useEffect(() => {
        if (loading && !userHasScrolledUp) {
            setTimeout(() => scrollToBottom(true), 50);
        }
    }, [loading, userHasScrolledUp]);    

    return (
        <>
            {post && post !== null ? (
                <Box width="full" height="100%" position="absolute" inset="0" background="white" display="flex" flexDirection="column">
                    <Box 
                        ref={scrollContainerRef}
                        flex="1"
                        overflowY="auto"
                        overflowX="hidden"
                        onScroll={handleScroll}
                    >
                        <Stack width="full" alignItems="center" boxSizing="border-box">
                            <Box 
                                width="100%" 
                                maxWidth="1080px"
                                boxSizing="border-box"
                                paddingX={{ base: 0, md: 0 }}
                            >
                                <ChatThread responses={responseList} />
                                
                                {/* Show streaming bubble if we're streaming and the post doesn't exist yet */}
                                {/* {isStreamingNewPost && (
                                    <StreamingBubble 
                                        streamingText={streamingText}
                                        postId={streamingAssistantPostId}
                                        vectorSearchResults={streamingVectorSearchResults}
                                    />
                                )} */}

                                {/* This ensures loading shows for follow-up messages even if previous stream has text */}
                                {shouldShowLoadingIndicator && (
                                    <HStack 
                                        className="fadeDelay" 
                                        padding={{ base: "12px 16px", md: 4 }} 
                                        width="100%" 
                                        maxWidth="100%"
                                        boxSizing="border-box"
                                    >
                                        <Card.Root
                                            width="100%"
                                            maxWidth={{ base: "100%", md: "90%" }}
                                            variant="subtle"
                                            background="none"
                                            boxSizing="border-box"
                                        >
                                            <Card.Body gap="0" paddingTop="0 !important" paddingBottom="0 !important">
                                                <Skeleton
                                                    alignItems="center"
                                                    justifyContent={isRTL ? "flex-end" : "flex-start"}
                                                    marginLeft={isRTL ? "0" : "4rem"}
                                                    marginRight={isRTL ? "1rem" : "0"}
                                                    height="5rem"
                                                    width="100%"
                                                    background="none"
                                                >
                                                    <img src={cairLogo} style={{ visibility: 'visible', height: "3rem" }} />
                                                </Skeleton>
                                                <Box width="100%" textAlign={isRTL ? "right" : "left"} padding={1}>
                                                    <Text>
                                                        {sourcesFound ? t("preparing_your_answer") : loadingText}
                                                        {loadingDots}
                                                    </Text>
                                                </Box>
                                            </Card.Body>
                                        </Card.Root>
                                    </HStack>
                                )}
                                <div ref={messagesEndRef} />
                            </Box>
                        </Stack>
                    </Box>
                    
                    <Box 
                        flexShrink="0"
                        width="full"
                        background="white"
                        display="flex"
                        justifyContent="center"
                    >
                        <Footer responding={true} />
                    </Box>
                </Box>
            ) : refreshChat === true ? (
                <div></div>
            ) : (
                <Box width="full" height="100%" position="absolute" inset="0" background="white" display="flex" flexDirection="column">
                    <Box
                        flex="1"
                        overflowY="auto"
                        overflowX="hidden"
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        paddingX={{ base: 2, md: 4 }}
                        paddingY={{ base: 4, md: 6 }}
                    >
                        <VStack
                            width="full"
                            maxWidth="720px"
                            gap={{ base: 3, md: 4 }}
                            alignItems="center"
                        >
                            {/* Book cover + title + description */}
                            <SuggestedQuestionsHeader />

                            {/* Welcome greeting */}
                            <EmptyState.Root className="fadeLong" padding={0}>
                                <EmptyState.Content gap={1}>
                                    <EmptyState.Title><UserFirstName fontSize="1.3125rem"/></EmptyState.Title>
                                </EmptyState.Content>
                            </EmptyState.Root>

                            {/* Centered input */}
                            <Box width="full">
                                <Footer responding={false} />
                            </Box>

                            {/* Personalized chips above the admin's. Each lands on its own —
                                the admin set is a single read and no longer waits on the
                                generated one, which is usually pre-warmed and otherwise holds
                                its slot with a pending pill. See SuggestionsPanel. */}
                            <SuggestionsPanel />
                        </VStack>
                        <div ref={messagesEndRef} />
                    </Box>
                </Box>
            )}
        </>
    );
};
